import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import styled, { useTheme } from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { EditorState } from 'draft-js'
import PencilSvg from 'static/images/pencil.svg'
import colors from 'configuration/colors'
import Button from 'components/mini/Button'
import CheckBox from 'components/mini/Checkbox'
import Modal from 'components/mini/Modal'
import FlexAlignCenter from 'components/styled/FlexAlignCenter'
import { Caption4, H3, P1, P2 } from 'components/styled/typography/typography'
import { ReactTooltipStyled } from 'components/styled/typography/typographyExtentions'
import { MinimalRichEditorWithLoom } from 'components/mini/DraftEditors'
import {
  contentFromHTMLWithVideo,
  contentToHTMLWithVideo
} from 'components/mini/DraftEditors/videoSeralization'
import RecordButton from 'components/mini/Loom/RecordButton'
import {
  EditorWrapper,
  ParagraphIcon
} from 'components/MyStudents/Announcements/styles'
import { hasText, isEmptyListInEditor } from 'utils/draftjs'
import useLoomData from 'hooks/useLoomData'
import { showModal } from 'actions/_modal'
import useLexicon from '../../../hooks/useLexicon'

const Alert = styled.div`
  text-align: center;
  font-size: 13px;
`

export const PencilIcon = styled.img.attrs({
  src: PencilSvg
})`
  width: 18px;
  height: 18px;
`

const AlertWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  color: #000;
  background: #f5f5f5;
`

const ButtonMinimal = styled.button`
  background: transparent;
  font-size: 16px;
  font-family: 'Lato', sans-serif;
  font-weight: 700;
`

const ButtonStyled = styled(ButtonMinimal)`
  min-width: 100px;
  padding: 7px 25px;
  margin-left: 15px;
  border-radius: 20px;
  color: ${colors.charcoal4};
`

export const CancelButton = styled(ButtonStyled)`
  margin: 0 12px 0 20px;
  border: 1px solid ${colors.aluminum};
`

const EditAnnouncement = props => {
  const {
    announcement,
    courseSection,
    contextData: {
      sessionId,
      isLoomRecordingEnabled,
      recoveryPath,
      recoveryAction: baseRecoveryAction
    },
    onCancel,
    onSave
  } = props

  const lexicon = useLexicon()
  const dispatch = useDispatch()
  const loomData = useLoomData(sessionId)
  const editor = useRef(null)
  const [initialized, setInitialized] = useState(false)
  const [focus, setFocus] = useState(false)
  const [applyToAllSections, setApplyToAllSections] = useState(false)
  const theme = useTheme()

  const recoveryAction = () => {
    baseRecoveryAction()
    dispatch(showModal('EDIT_ANNOUNCEMENT', props))
  }

  const initialEditorState = () => {
    const initialContent = loomData?.recovery?.content || announcement.text
    if (initialContent) {
      return EditorState.createWithContent(
        contentFromHTMLWithVideo(initialContent)
      )
    } else {
      return EditorState.createEmpty()
    }
  }

  const [editorState, setEditorState] = useState(initialEditorState())

  const getText = () => {
    const content = editorState.getCurrentContent()
    return content.hasText()
      ? contentToHTMLWithVideo(editorState.getCurrentContent())
      : null
  }

  useLayoutEffect(() => {
    setTimeout(() => {
      focusText()
      setInitialized(true)
    }, 300)
  }, [])

  // CRAPPY CRAPPY hack around MaterialUI creating a hidden div for each Menu component (one per announcement)
  // which was causing issues with being able to focus the RTE in this modal
  useEffect(() => {
    const muiStuff = document.querySelectorAll('div[role="presentation"]')
    muiStuff.forEach(el => {
      el.style.display = 'none'
    })
    return () => {
      muiStuff.forEach(el => {
        el.style.display = 'block'
      })
    }
  }, [])

  const focusText = () => {
    editor.current?.focus()
  }

  return (
    <>
      <Modal width={663} top={50} maxPercentHeight={80} allowInnerScroll>
        <Alert>
          <AlertWrapper>
            <FlexAlignCenter
              style={{
                justifyContent: 'space-between',
                padding: '16px 20px',
                background: '#fff',
                borderBottom: '1px solid #CCCCCC',
                borderTopLeftRadius: '10px',
                borderTopRightRadius: '10px'
              }}
            >
              <FlexAlignCenter>
                <PencilIcon />
                <H3 as="h3" lato light style={{ marginLeft: '10px' }}>
                  Edit Announcement
                </H3>
              </FlexAlignCenter>
              <P1>{courseSection.name_clean}</P1>
            </FlexAlignCenter>
            <div
              style={{
                padding: '20px 20px 0'
              }}
            >
              <EditorWrapper focus={focus} onClick={focusText}>
                <P2
                  className={
                    isEmptyListInEditor(editorState) ? 'empty-list' : ''
                  }
                >
                  <MinimalRichEditorWithLoom
                    ref={editor}
                    editorState={editorState}
                    onChange={es => {
                      setEditorState(es)
                    }}
                    onBlur={() => {
                      setFocus(false)
                    }}
                    onFocus={() => {
                      setFocus(true)
                    }}
                    loomData={loomData}
                    initialized={initialized}
                    sessionId={sessionId}
                  />
                </P2>

                {isLoomRecordingEnabled && (
                  <FlexAlignCenter
                    style={{
                      position: 'absolute',
                      left: '22px',
                      bottom: '10px'
                    }}
                  >
                    <RecordButton
                      text="+ Insert a Recording"
                      sessionId={sessionId}
                      recoveryPath={recoveryPath}
                      recoveryAction={recoveryAction}
                      externalTooltipId="edit-announcement-record-tooltip"
                    />
                  </FlexAlignCenter>
                )}

                <FlexAlignCenter
                  style={{
                    position: 'absolute',
                    right: '10px',
                    bottom: '10px'
                  }}
                >
                  <ParagraphIcon
                    style={{ marginRight: '7px' }}
                    data-tip="Highlight text to see formatting options."
                    data-for="edit-announcement-format-tooltip"
                  />
                </FlexAlignCenter>
              </EditorWrapper>
            </div>
            <FlexAlignCenter
              style={{
                justifyContent: 'space-between',
                padding: '0 20px 20px'
              }}
            >
              <FlexAlignCenter>
                <CheckBox
                  checked={applyToAllSections}
                  id="apply_to_all_sections"
                  name="aapply_to_all_sections"
                  onChange={() => {
                    setApplyToAllSections(apply => !apply)
                  }}
                />
                <Caption4 as="label" htmlFor="apply_to_all_sections">
                  Apply to all {lexicon.section.toLowerCase()}s that received
                  this announcement.
                </Caption4>
              </FlexAlignCenter>
              <FlexAlignCenter>
                <CancelButton
                  className={`quest-btn btn-c btn-c-${theme?.style}`}
                  onClick={onCancel}
                >
                  Cancel
                </CancelButton>
                <Button
                  className={`quest-btn btn-a btn-a-${theme?.style} small ${
                    !hasText(editorState) ? 'disabled' : ''
                  }`}
                  style={{
                    marginLeft: '10px',
                    paddingLeft: '40px',
                    paddingRight: '40px',
                    cursor: 'pointer'
                  }}
                  text="Save"
                  onClick={
                    hasText(editorState)
                      ? () => {
                          onSave(getText(), applyToAllSections)
                        }
                      : null
                  }
                />
              </FlexAlignCenter>
            </FlexAlignCenter>
          </AlertWrapper>
        </Alert>
      </Modal>
      {/* https://github.com/wwayne/react-tooltip/issues/130 */}
      <ReactTooltipStyled
        id="edit-announcement-record-tooltip"
        effect="solid"
        place="right"
        raiseAboveModal
      />
      <ReactTooltipStyled
        id="edit-announcement-format-tooltip"
        effect="solid"
        raiseAboveModal
      />
    </>
  )
}

EditAnnouncement.propTypes = {
  announcement: PropTypes.object.isRequired,
  courseSection: PropTypes.object.isRequired,
  contextData: PropTypes.object.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired
}

export default EditAnnouncement
